import { Autocomplete, AutocompleteChangeDetails, Button, Checkbox, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { resetUserInput, setDisplayName, setEmail, setEmailDisplayName, setInputExpand, setIsLocked, setPassword, setSelectedUserId, setSelectedUserIndex, setTriggerRefreshTable, setUserGroup, setUserInput, setWorkingEmail, userManagementSelector } from './reducers/UserManagementReducer';
import UserApi from './api/UserApi';
import UserModel from '../Login/models/UserModel';
import { UserGroupEnum } from './UserGroupEnum';
import UserGroupModel from './models/UserGroupModel';

const UserManagementInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const userManagementViewData = useSelector(userManagementSelector);

    useEffect(() => {
        if (userManagementViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [userManagementViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetUserInput());
        dispatch(setSelectedUserId(-1));
        dispatch(setSelectedUserIndex(-1));
    }

    useEffect(() => {
        if (userManagementViewData.selectedUserId > -1) {
            selectUserById(userManagementViewData.selectedUserId);
        }
    }, [userManagementViewData.selectedUserId])

    const selectUserById = (id: number) => {
        dispatch(showLoading(true));
        UserApi.GetById({
            id: id
        }).then((response: BaseResponse<UserModel>) => {
            if (response.success) {
                let userInput = Utility.CloneObject(userManagementViewData.input) as UserModel;

                userInput.id = response.data.id;
                userInput.email = response.data.email;
                userInput.userGroup = response.data.userGroup;
                userInput.displayName = response.data.displayName;
                userInput.workingEmail = response.data.workingEmail;
                userInput.emailDisplayName = response.data.emailDisplayName;
                userInput.isLocked = response.data.isLocked;
                userInput.password = response.data.password;

                dispatch(setUserInput(userInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const save = () => {
        try {
            validate();

            UserApi.Save(userManagementViewData.input)
                .then((response: BaseResponse<UserModel>) => {
                    if (response.success) {
                        if (userManagementViewData.input.id == -1) {
                            Utility.Alert.Success("User added successful");
                        } else {
                            Utility.Alert.Success("User updated successful");
                        }

                        refreshInput();

                        dispatch(setTriggerRefreshTable(new Date().getTime()));
                    }
                    else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (userManagementViewData.input.email.length === 0) {
            throw "Email is required";
        }

        if (userManagementViewData.input.userGroup.length === 0) {
            throw "User group is required";
        }

        if (userManagementViewData.input.displayName.length === 0) {
            throw "Display name is required";
        }

        if (userManagementViewData.input.workingEmail.length === 0) {
            throw "Working email is required";
        }
    }

    let getUserGroups = () => {
        return _.map(Object.keys(UserGroupEnum), (enumKey) => {
            var indexOfKey = Object.keys(UserGroupEnum).indexOf(enumKey as unknown as UserGroupEnum);
            return {
                value: enumKey,
                displayName: Object.values(UserGroupEnum)[indexOfKey]
            } as UserGroupModel
        })
    }

    let getUserGroupByStringValue = (enumKey: string) => {
        var indexOfKey = Object.keys(UserGroupEnum).indexOf(enumKey as unknown as UserGroupEnum);

        if (indexOfKey == -1) {
            return null;
        }

        return {
            value: enumKey,
            displayName: Object.values(UserGroupEnum)[indexOfKey]
        } as UserGroupModel
    }

    return (
        <>
            <CustomAccordion
                title="Input"
                defaultOpen={true}
                className="top-quick-input"
                open={userManagementViewData.inputExpand}
                onOpenChange={(isOpen: boolean) => {
                    dispatch(setInputExpand(isOpen));
                }}
            >
                <Grid container>
                    <Grid item lg={3} xs={12}
                        className='cargo-input-component'
                    >
                        <Grid container className='cargo-input-component'>
                            <Grid xs={10} lg={9}>
                                <TextField
                                    label="Emails"
                                    fullWidth
                                    value={userManagementViewData.input.email}
                                    onChange={(event) => {
                                        dispatch(setEmail(event.target.value));
                                    }}
                                    className="tonnage-input-full-tc"
                                />
                            </Grid>
                            <Grid item xs={2} lg={3}>
                                <Tooltip title="Reset">
                                    <Button
                                        className="clear-input-button"
                                        color="success"
                                        variant="contained"
                                        tabIndex={-1}
                                        onClick={() => {
                                            refreshInput();
                                        }}
                                    >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <TextField
                            label="Display Name"
                            fullWidth
                            value={userManagementViewData.input.displayName}
                            onChange={(event) => {
                                dispatch(setDisplayName(event.target.value));
                            }}
                        />
                    </Grid>
                    <Grid item lg={4} xs={12}
                        className="cargo-input-component"
                    >
                        <Autocomplete
                            className='cargo-input-component'
                            options={getUserGroups()}
                            renderInput={(params) => <TextField {...params} label="User Group" />}
                            getOptionLabel={(option: UserGroupModel) => {
                                return option.displayName;
                            }}
                            value={getUserGroupByStringValue(userManagementViewData.input.userGroup)}
                            onChange={(event: React.SyntheticEvent, value: UserGroupModel | null, reason: string, details?: AutocompleteChangeDetails<UserGroupModel> | undefined) => {
                                dispatch(setUserGroup(value ? value.value : ""));
                            }}
                        />
                        <TextField
                            className='cargo-input-component'
                            label="Working Email"
                            fullWidth
                            value={userManagementViewData.input.workingEmail}
                            onChange={(event) => {
                                dispatch(setWorkingEmail(event.target.value));
                            }}
                        />
                    </Grid>
                    <Grid item lg={4} xs={12}
                        className="cargo-input-component"
                    >
                        <TextField
                            className='cargo-input-component'
                            label="Password"
                            type='password'
                            fullWidth
                            value={!userManagementViewData.input.password || userManagementViewData.input.password.length == 0 ? "" : userManagementViewData.input.password}
                            onChange={(event) => {
                                dispatch(setPassword(event.target.value));
                            }}
                        />
                        <TextField
                            className='cargo-input-component'
                            label="Email Display Name"
                            fullWidth
                            value={userManagementViewData.input.emailDisplayName}
                            onChange={(event) => {
                                dispatch(setEmailDisplayName(event.target.value));
                            }}
                        />
                    </Grid>
                    <Grid item lg={1} xs={12}
                    >
                        <Button
                            onClick={save}
                            variant="outlined"
                        >
                            Save
                        </Button>
                        <FormControlLabel control={
                            <Checkbox
                                checked={userManagementViewData.input.isLocked}
                                onChange={(event) => {
                                    dispatch(setIsLocked(event.currentTarget.checked));
                                }}
                            />}
                            className="tonnage-input-grabs"
                            label="Locked"
                        />
                    </Grid>
                </Grid>
            </CustomAccordion>
        </>
    )
};

export default UserManagementInput;